import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import useIsMobile from "../../hooks/useIsMobile";

// ─────────────────────────────────────────────────────────────────────────────
// PULSO DE VENTAS (28-ago-2026)
// Cabecera comercial de /quotations: la visión de VENTAS del vendedor (Nico).
// Un solo endpoint (GET /sii/pulso-ventas) alimenta 4 tarjetas:
//   💰 Facturado este mes  — vs mes pasado y vs mismo mes del año pasado
//   📊 Pipeline vivo       — $ cotizado en juego + conversión 90 días
//   😴 Clientes dormidos   — historial real sin comprar hace >45 días: a quién
//                            llamar esta semana (el oro del vendedor)
//   🏆 Top del mes         — clientes y piezas
// SOLO datos de ventas — nada de costos/utilidad (un seller puede verlo todo).
// Roles: admin/accountant/seller; para otros el componente no renderiza nada.
// Estilo: tarjetas PulseNegocio (fondo #242424, borde #2e2e2e, radio 12).
// Degradación: mientras carga → skeleton; si el fetch falla → "—" y subtítulos
// "sin datos", nunca rompe la pantalla de cotizaciones.
// ─────────────────────────────────────────────────────────────────────────────

const SEM = { green: "#2ecc71", red: "#e74c3c", amber: "#f39c12", blue: "#3498db", gray: "#8a8a8a" };

// Monto compacto para números grandes (millones → "M"); exacto en el tooltip.
const clpC = v => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toLocaleString("es-CL", { maximumFractionDigits: 1 })}M`;
  return `$${Math.round(n).toLocaleString("es-CL")}`;
};
const clp = v => (v == null || isNaN(v) ? "—" : `$${Math.round(Number(v)).toLocaleString("es-CL")}`);

// "2025-08" → "ago 2025" (para la sub-línea de estacionalidad)
const nombrePeriodo = per => {
  if (!per || !/^\d{4}-\d{2}$/.test(per)) return "";
  const [y, m] = per.split("-").map(Number);
  const mes = new Date(y, m - 1, 1).toLocaleDateString("es-CL", { month: "short" });
  return `${mes} ${y}`;
};

// Variación % con flecha de color. base <= 0 → "sin base" (no se inventa un %).
const Delta = ({ actual, base, etiqueta }) => {
  if (actual == null || base == null || base <= 0) {
    return <span style={{ color: "#777" }}>vs {etiqueta}: sin base</span>;
  }
  const pct = Math.round(((actual - base) / base) * 100);
  const sube = pct >= 0;
  return (
    <span title={`${etiqueta}: ${clp(base)}`}>
      <span style={{ color: sube ? SEM.green : SEM.red, fontWeight: 700 }}>
        {sube ? "▲" : "▼"} {sube ? "+" : ""}{pct}%
      </span>
      <span style={{ color: "#8a8a8a" }}> vs {etiqueta}</span>
    </span>
  );
};

const Skeleton = ({ w = "60%" }) => (
  <div style={{ height: 24, width: w, background: "#2e2e2e", borderRadius: 6, opacity: 0.6 }} />
);

// Props opcionales (28-ago, tarjetas cliqueables — las cablea Quotations):
//   onFiltrar(key)         — aplica un filtro de la tabla de cotizaciones de
//                            abajo (ej.: "enviadas" al clicar Pipeline vivo).
//   onBuscarCliente(nombre) — setea el buscador de la tabla con ese cliente
//                            (filas de clientes dormidos).
const PulsoVentas = ({ onFiltrar, onBuscarCliente }) => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const puedeVer = auth?.can?.("ver_pulso_ventas") || false;

  // undefined = cargando · null = falló · objeto = datos
  const [data, setData] = useState(undefined);
  const [dormExpandido, setDormExpandido] = useState(false);

  useEffect(() => {
    if (!puedeVer) return;
    backend.get("sii/pulso-ventas")
      .then(res => setData(res.data || null))
      .catch(() => setData(null));
  }, [puedeVer]);

  if (!puedeVer) return null; // operator no lo necesita (y el endpoint le daría 403)

  const cargando = data === undefined;
  const mes  = data?.mesActual || null;
  const ant  = data?.mesAnterior || null;
  const anio = data?.mismoMesAnioPasado || null;
  const pipe = data?.pipeline || null;
  const dormidos = data?.clientesDormidos || [];
  const topClientes = data?.topClientesMes || [];
  const topPiezas   = data?.topPiezasMes || [];
  const hayDormidos = dormidos.length > 0;

  // ── tarjeta base (estilo PulseNegocio) ──────────────────────────────────────
  // onClick + titleHint (28-ago): la tarjeta entera es cliqueable si vienen.
  const Card = ({ icon, titulo, urgente, span, onClick, titleHint, children }) => (
    <div
      onClick={onClick}
      title={titleHint}
      style={{
        gridColumn: span || "auto",
        background: urgente ? "#241a12" : "#242424",
        border: `1px solid ${urgente ? "#4a3410" : "#2e2e2e"}`,
        borderRadius: 12, padding: "13px 14px",
        minWidth: 0, display: "flex", flexDirection: "column",
        cursor: onClick ? "pointer" : "default",
        transition: "background .15s, border-color .15s",
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.background = urgente ? "#2a1f15" : "#2a2a2a"; }}
      onMouseLeave={e => { if (onClick) e.currentTarget.style.background = urgente ? "#241a12" : "#242424"; }}
    >
      <div style={{
        fontSize: 10, fontWeight: 700, color: urgente ? SEM.amber : "#8a8a8a",
        textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {icon} {titulo}
      </div>
      {children}
    </div>
  );

  const filaMini = (rank, texto, monto, extra) => (
    <div key={rank + texto} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 12, padding: "2px 0" }}>
      <span style={{ color: "#bbb", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
        <span style={{ color: SEM.amber, fontWeight: 700, marginRight: 5 }}>{rank}</span>
        {texto}
        {extra && <span style={{ color: "#777" }}> {extra}</span>}
      </span>
      <span style={{ color: SEM.green, fontWeight: 700, flexShrink: 0, fontVariantNumeric: "tabular-nums" }} title={clp(monto)}>
        {clpC(monto)}
      </span>
    </div>
  );

  const dormVisibles = dormExpandido ? dormidos : dormidos.slice(0, 3);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "2px 0 9px" }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: ".07em" }}>
          Pulso de ventas
        </span>
        <span style={{ fontSize: 10, color: "#777" }}>
          {new Date().toLocaleDateString("es-CL", { month: "long", year: "numeric" })}
        </span>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
        gap: 8,
      }}>

        {/* 💰 Facturado este mes */}
        <Card icon="💰" titulo="Facturado este mes"
          onClick={() => navigate("/contabilidad")}
          titleHint="Ver el detalle de facturación en Contabilidad">
          {cargando ? <Skeleton /> : (
            <div style={{ fontSize: 24, fontWeight: 800, color: mes && mes.neto < 0 ? SEM.red : SEM.green, lineHeight: 1.1, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              title={mes ? `Neto facturado ${clp(mes.neto)} · ${mes.nFacturas} factura${mes.nFacturas === 1 ? "" : "s"}` : undefined}>
              {mes ? clpC(mes.neto) : "—"}
            </div>
          )}
          <div style={{ fontSize: 10.5, color: "#8a8a8a", marginTop: 5, display: "flex", flexDirection: "column", gap: 2 }}>
            {cargando ? <span>cargando…</span> : mes ? (
              <>
                <Delta actual={mes.neto} base={ant?.neto} etiqueta="mes pasado" />
                <Delta actual={mes.neto} base={anio?.neto} etiqueta={nombrePeriodo(anio?.periodo) || "año pasado"} />
                <span>{mes.nFacturas} factura{mes.nFacturas === 1 ? "" : "s"} neto (NC descontadas)</span>
              </>
            ) : <span>sin datos de facturación</span>}
          </div>
        </Card>

        {/* 📊 Pipeline vivo */}
        <Card icon="📊" titulo="Pipeline vivo"
          onClick={onFiltrar ? () => onFiltrar("enviadas") : undefined}
          titleHint={onFiltrar ? "Filtrar la tabla de abajo: cotizaciones enviadas" : undefined}>
          {cargando ? <Skeleton /> : (
            <div style={{ fontSize: 24, fontWeight: 800, color: SEM.blue, lineHeight: 1.1, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              title={pipe ? `Cotizado en juego ${clp(pipe.cotizadoVivo)} (enviadas + en seguimiento)` : undefined}>
              {pipe ? clpC(pipe.cotizadoVivo) : "—"}
            </div>
          )}
          <div style={{ fontSize: 10.5, color: "#8a8a8a", marginTop: 5, display: "flex", flexDirection: "column", gap: 2 }}>
            {cargando ? <span>cargando…</span> : pipe ? (
              <>
                <span>{pipe.nVivas} cotización{pipe.nVivas === 1 ? "" : "es"} en juego · montos con IVA</span>
                <span>
                  conversión 90d:{" "}
                  {pipe.conversion90d != null ? (
                    <span style={{ color: pipe.conversion90d >= 50 ? SEM.green : SEM.amber, fontWeight: 700 }}>
                      {pipe.conversion90d}%
                    </span>
                  ) : "—"}
                  {" "}<span>({pipe.ganadas90d} de {pipe.enviadas90d} enviadas)</span>
                </span>
              </>
            ) : <span>sin datos de cotizaciones</span>}
          </div>
        </Card>

        {/* 😴 Clientes dormidos — ámbar si hay; el oro del vendedor */}
        <Card icon="😴" titulo={`Clientes dormidos${hayDormidos ? ` (${dormidos.length})` : ""}`} urgente={hayDormidos} span="span 2">
          {cargando ? <Skeleton w="80%" /> : !data ? (
            <div style={{ fontSize: 12, color: "#777", padding: "4px 0" }}>—</div>
          ) : !hayDormidos ? (
            <div style={{ fontSize: 12, color: SEM.green, padding: "4px 0" }}>
              Sin clientes dormidos — la cartera está comprando al día.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 10.5, color: SEM.amber, fontWeight: 700, marginBottom: 5 }}>
                📞 Con historial y sin comprar hace más de 45 días — llámalos esta semana.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {dormVisibles.map(c => (
                  <div key={c.rut}
                    onClick={onBuscarCliente ? () => onBuscarCliente(c.cliente) : undefined}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 12, padding: "2px 0", cursor: onBuscarCliente ? "pointer" : "default", borderRadius: 4 }}
                    onMouseEnter={e => { if (onBuscarCliente) e.currentTarget.style.background = "#2e2418"; }}
                    onMouseLeave={e => { if (onBuscarCliente) e.currentTarget.style.background = ""; }}
                    title={`${c.cliente} · última compra ${c.ultimaCompra ? new Date(c.ultimaCompra).toLocaleDateString("es-CL") : "—"} · ${c.nFacturas12m} facturas en 12m${onBuscarCliente ? " — click: ver sus cotizaciones abajo" : ""}`}>
                    <span style={{ color: "#ddd", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                      {c.cliente}
                    </span>
                    <span style={{ color: c.diasSinComprar >= 90 ? SEM.red : SEM.amber, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0, fontSize: 11 }}>
                      {c.diasSinComprar} días
                    </span>
                    <span style={{ color: "#8a8a8a", whiteSpace: "nowrap", flexShrink: 0, fontSize: 11, fontVariantNumeric: "tabular-nums" }} title={`Promedio mensual ${clp(c.promedioMensual)}`}>
                      prom {clpC(c.promedioMensual)}/mes
                    </span>
                  </div>
                ))}
              </div>
              {dormidos.length > 3 && (
                <button
                  onClick={() => setDormExpandido(e => !e)}
                  style={{
                    marginTop: 6, alignSelf: "flex-start", background: "transparent",
                    border: "1px solid #4a3410", color: SEM.amber, borderRadius: 5,
                    padding: "3px 10px", fontSize: 10, fontWeight: 700, cursor: "pointer",
                  }}
                >
                  {dormExpandido ? "▲ ver menos" : `▼ ver los ${dormidos.length}`}
                </button>
              )}
            </>
          )}
        </Card>

        {/* 🏆 Top del mes: clientes y piezas */}
        <Card icon="🏆" titulo="Top del mes" span={isMobile ? "span 2" : "span 4"}>
          {cargando ? <Skeleton w="70%" /> : !data ? (
            <div style={{ fontSize: 12, color: "#777", padding: "4px 0" }}>—</div>
          ) : (
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
              gap: isMobile ? 12 : 20,
            }}>
              <div style={{ minWidth: 0, cursor: "pointer" }}
                onClick={() => navigate("/contabilidad?tab=clientes")}
                title="Ver el ranking completo de clientes en Contabilidad">
                <div style={{ fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>
                  Clientes <span style={{ color: "#666" }}>↗</span>
                </div>
                {topClientes.length === 0
                  ? <div style={{ fontSize: 12, color: "#777" }}>Sin facturación este mes</div>
                  : topClientes.map((c, i) => filaMini(i + 1, c.cliente, c.neto, `· ${c.nFacturas} fact.`))}
              </div>
              <div style={{ minWidth: 0, cursor: "pointer" }}
                onClick={() => navigate("/contabilidad?tab=piezas")}
                title="Ver el ranking completo de piezas en Contabilidad">
                <div style={{ fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>
                  Piezas <span style={{ color: "#666" }}>↗</span>
                </div>
                {topPiezas.length === 0
                  ? <div style={{ fontSize: 12, color: "#777" }}>Sin ventas de piezas este mes</div>
                  : topPiezas.map((p, i) => filaMini(i + 1, `${p.sku ? `${p.sku} · ` : ""}${p.nombre}`, p.ingreso, `· ${p.unidades} un.`))}
              </div>
            </div>
          )}
        </Card>

      </div>
    </div>
  );
};

export default PulsoVentas;
